/**
 * Sync como long-running job.
 *
 * O disparo (POST /sync-agent) retorna na hora com o `sessaoId`; o progresso
 * acompanha-se por polling HTTP (GET /sync-progress/:sessaoId) — não existe
 * canal push backend→frontend, e polling de 2s é o padrão do projeto
 * (react-query). O progresso em si é reportado pelo agente via WebSocket
 * (mensagens `sync_progress`) e mantido em memória no backend.
 *
 * Consumidores: LayoutUsuario (botão global), PanelRouter (tela do Router),
 * AssistentePanel (aplicar plano) e SyncLogModal (progresso ao vivo + cancelar).
 */

import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { toastError, toastSuccess, toastWarning } from '@/lib/toast';

export type SyncPhaseDTO = 'fetch' | 'plan' | 'apply' | 'squid' | 'done';

export type SyncProgressDTO = {
  phase: SyncPhaseDTO | string;
  area: string | null;
  area_index: number | null;
  area_total: number | null;
  acoes_done: number | null;
  acoes_total: number | null;
  updated_at: string;
} | null;

type SyncProgressFullResponse = {
  ok: boolean;
  running: boolean;
  progress?: SyncProgressDTO;
  sessao?: SyncSessaoDTO;
};

/** Espelha packages/shared/src/dto/sync_log.ts (import type só no tsc — a
 *  build do frontend quebra com import de runtime de @webip/shared). */
type SyncSessaoStatusDTO = 'running' | 'success' | 'error' | 'partial' | 'cancelled';

export type SyncSessaoDTO = {
  id: number;
  appliance_id: number;
  data_inicio: string;
  data_fim?: string | null;
  status: SyncSessaoStatusDTO;
  erro?: string | null;
};

/** Rótulos por fase — a barra fica indeterminada durante fetch/plan. */
const PHASE_LABEL: Record<string, string> = {
  fetch: 'Lendo estado do equipamento',
  plan: 'Calculando diferenças',
  apply: 'Aplicando configuração',
  squid: 'Configurando proxy (Squid)',
  done: 'Finalizando',
};

export const phaseLabel = (phase: string | null | undefined): string =>
  (phase && PHASE_LABEL[phase]) || 'Sincronizando';

/**
 * Dispara o sync (long-running job) e devolve o `sessaoId` imediatamente.
 * `jaRodando` marca o 409 de concorrência (já existe sync em andamento).
 */
export async function dispararSync(
  applianceId: number,
): Promise<{ ok: boolean; sessaoId: number | null; jaRodando?: boolean; erro?: string }> {
  try {
    const res = await axios.post<{
      ok: boolean;
      sessaoId?: number;
      status?: string;
      erro?: string;
    }>(`/api/appliance/${applianceId}/sync-agent`, {}, { withCredentials: true });
    if (res.data?.ok === false) {
      return { ok: false, sessaoId: res.data.sessaoId ?? null, erro: res.data.erro };
    }
    return { ok: true, sessaoId: res.data.sessaoId ?? null };
  } catch (err: any) {
    if (err?.response?.status === 409) {
      const data = err.response.data ?? {};
      return {
        ok: false,
        jaRodando: true,
        sessaoId: data.sessaoId ?? null,
        erro: data.erro ?? 'Já existe uma sincronização em andamento',
      };
    }
    return { ok: false, sessaoId: null, erro: err?.response?.data?.msg ?? 'Erro ao sincronizar appliance.' };
  }
}

/** Pede o cancelamento do sync em andamento (requer agente com sync.cancel). */
export async function cancelarSync(applianceId: number, sessaoId: number): Promise<{ ok: boolean; erro?: string }> {
  try {
    const res = await axios.post<{ ok: boolean }>(
      `/api/appliance/${applianceId}/sync-agent/${sessaoId}/cancel`,
      {},
      { withCredentials: true },
    );
    return { ok: res.data?.ok !== false };
  } catch (err: any) {
    return { ok: false, erro: err?.response?.data?.msg ?? 'Não foi possível cancelar o sincronismo' };
  }
}

/** Polling do progresso (2s enquanto habilitado). Para quando a sessão termina
 *  ou quando `sessaoId` volta a null — o chamador zera ao tratar o fim. */
export function useSyncProgress(applianceId: number | null, sessaoId: number | null) {
  return useQuery<SyncProgressFullResponse>(
    ['sync-progress', applianceId, sessaoId],
    async () => {
      const res = await axios.get<SyncProgressFullResponse>(
        `/api/appliance/${applianceId}/sync-progress/${sessaoId}`,
        { withCredentials: true },
      );
      return res.data;
    },
    {
      enabled: !!applianceId && !!sessaoId,
      refetchInterval: 2000,
    },
  );
}

/** Toast conforme o status final da sessão (reaproveita as mensagens existentes). */
export function toastStatusFinal(status: SyncSessaoStatusDTO): void {
  if (status === 'success') {
    toastSuccess('Appliance sincronizada com sucesso!');
  } else if (status === 'partial') {
    toastWarning('Sincronização concluída com erros em algumas ações. Verifique o log de sincronismo.');
  } else if (status === 'cancelled') {
    toastWarning('Sincronização cancelada. As ações já aplicadas permanecem — verifique o log de sincronismo.');
  } else {
    toastError('Sincronização executada, mas todas as ações falharam. Verifique o log de sincronismo.');
  }
}

export type SyncJobState = {
  /** Sessão em acompanhamento (null = nenhuma). */
  sessaoId: number | null;
  /** true do clique no botão até o toast final. */
  rodando: boolean;
  /** Último progresso reportado pelo agente (null enquanto nada chegou). */
  progresso: SyncProgressDTO;
  disparar: () => Promise<void>;
  cancelar: () => Promise<void>;
};

/**
 * Ciclo completo do sync assíncrono para um componente: dispara, acompanha por
 * polling, exibe o toast do status final e libera o botão. O toast é emitido
 * uma única vez, na transição running → finalizada.
 */
export function useSyncJob(applianceId: number | null): SyncJobState {
  const [sessaoId, setSessaoId] = useState<number | null>(null);
  const [disparando, setDisparando] = useState(false);

  // Trocar de appliance larga a sessão da anterior (o polling usaria o par
  // appliance/sessão errado e o botão mostraria o progresso de outra).
  useEffect(() => {
    setSessaoId(null);
  }, [applianceId]);

  // O sessaoId só chega pela resposta do disparo: após recarregar a página (ou
  // com o sync disparado em outra aba/tela/pelo assistente) o hook não sabe
  // dele. Enquanto ocioso, pergunta ao backend se há sessão `running` e passa
  // a acompanhá-la.
  const emAndamento = useQuery<{ ok: boolean; sessaoId: number | null }>(
    ['sync-running', applianceId],
    async () => {
      const res = await axios.get<{ ok: boolean; sessaoId: number | null }>(
        `/api/appliance/${applianceId}/sync-running`,
        { withCredentials: true },
      );
      return res.data;
    },
    {
      enabled: !!applianceId && sessaoId == null && !disparando,
      refetchInterval: 15000,
    },
  );

  const query = useSyncProgress(applianceId, sessaoId);
  const data = query.data;
  const rodando = disparando || (sessaoId != null && !!data?.running);
  const progresso = data?.progress ?? null;

  // Fim do sync: toast do status final e libera o botão. Guard pelo sessaoId
  // evita toast duplicado entre re-renders.
  const finalizou = sessaoId != null && data != null && !data.running;
  const [tratada, setTratada] = useState<number | null>(null);
  useEffect(() => {
    if (sessaoId == null || !finalizou || tratada === sessaoId) return;
    setTratada(sessaoId);
    const status = data?.sessao?.status ?? 'error';
    toastStatusFinal(status);
    setSessaoId(null);
  }, [sessaoId, finalizou, tratada, data]);

  // `tratada` impede readotar a sessão recém-finalizada enquanto o cache do
  // sync-running ainda a aponta.
  const sessaoRodando = emAndamento.data?.sessaoId ?? null;
  useEffect(() => {
    if (sessaoId != null || disparando || sessaoRodando == null || tratada === sessaoRodando) return;
    setSessaoId(sessaoRodando);
  }, [sessaoId, disparando, sessaoRodando, tratada]);

  const disparar = async () => {
    if (!applianceId || disparando || rodando) return;
    setDisparando(true);
    const res = await dispararSync(applianceId);
    if (!res.ok) {
      if (res.jaRodando && res.sessaoId) {
        // Já tem sync em andamento: passa a acompanhar essa sessão em vez de falhar.
        toastWarning(res.erro ?? 'Já existe uma sincronização em andamento — acompanhando.');
        setTratada(null);
        setSessaoId(res.sessaoId);
      } else {
        toastError(res.erro ?? 'Erro ao sincronizar appliance.');
      }
    } else if (res.sessaoId) {
      setTratada(null);
      setSessaoId(res.sessaoId);
    } else {
      toastError('Sincronismo disparado, mas o backend não informou a sessão.');
    }
    setDisparando(false);
  };

  const cancelar = async () => {
    if (!applianceId || !sessaoId) return;
    const res = await cancelarSync(applianceId, sessaoId);
    if (!res.ok) {
      toastError(res.erro ?? 'Não foi possível cancelar o sincronismo');
    }
  };

  return { sessaoId, rodando, progresso, disparar, cancelar };
}